import type { Lane, NoteEvent } from "../types";

export const GRID_COLS = 5;
export const GRID_ROWS = 3;
/** Seconds a gray circle squats in a grid cell before it clears itself. */
export const GRAY_TTL = 20;

export const POINTS = {
  circle: 25,
  combo: 150,
  fullRow: 400,
} as const;

export interface Cell {
  kind: "color" | "gray";
  /**
   * Color slot for colored cells. The actual RGB resolves against the live
   * palette when drawn, so cells shift hue in unison with the background.
   */
  slot: "primary" | "accent";
  /** Song-time when a gray cell expires. */
  expiresAt?: number;
}

export interface CollectResult {
  points: number;
  filledCell: { row: number; col: number } | null;
  /** Cleared cell columns when 3 same-slot cells combo'd. */
  combo: { row: number; slot: "primary" | "accent"; cols: number[] } | null;
  fullRow: number | null;
}

/**
 * The score grid: 3 rows (one per lane) × 5 cells.
 * - Colored circle → fills next free cell of its lane's row.
 * - Gray circle → squats a cell for GRAY_TTL seconds, blocking combos/rows.
 * - 3 same-color cells in a row → combo bonus, those cells clear.
 * - Full row of colored cells (no gray) → big bonus, row resets.
 */
export class GridState {
  readonly rows: (Cell | null)[][] = Array.from({ length: GRID_ROWS }, () =>
    Array<Cell | null>(GRID_COLS).fill(null),
  );

  /** Applies a collected note. Row index mirrors lane index. */
  collect(note: NoteEvent, now: number): CollectResult {
    const row = note.lane as Lane;
    const cells = this.rows[row]!;
    const col = cells.indexOf(null);
    const result: CollectResult = { points: 0, filledCell: null, combo: null, fullRow: null };

    if (note.kind === "gray") {
      if (col !== -1) {
        cells[col] = { kind: "gray", slot: note.slot, expiresAt: now + GRAY_TTL };
        result.filledCell = { row, col };
      }
      return result;
    }

    result.points += POINTS.circle;
    if (col === -1) return result; // row jammed (grays) — score the circle, no fill

    cells[col] = { kind: "color", slot: note.slot };
    result.filledCell = { row, col };

    // Combo: 3 cells of the same color slot anywhere in the row.
    const bySlot = new Map<"primary" | "accent", number[]>();
    for (let i = 0; i < GRID_COLS; i++) {
      const c = cells[i];
      if (c && c.kind === "color") {
        const list = bySlot.get(c.slot) ?? [];
        list.push(i);
        bySlot.set(c.slot, list);
      }
    }
    for (const [slot, cols] of bySlot) {
      if (cols.length >= 3) {
        for (const i of cols) cells[i] = null;
        result.points += POINTS.combo;
        result.combo = { row, slot, cols };
        break;
      }
    }

    // Full row: every cell colored, no grays.
    if (!result.combo && cells.every((c) => c !== null && c.kind === "color")) {
      for (let i = 0; i < GRID_COLS; i++) cells[i] = null;
      result.points += POINTS.fullRow;
      result.fullRow = row;
    }

    return result;
  }

  /** Expires gray squatters. Returns true when anything changed. */
  update(now: number): boolean {
    let changed = false;
    for (const cells of this.rows) {
      for (let i = 0; i < GRID_COLS; i++) {
        const c = cells[i];
        if (c && c.kind === "gray" && c.expiresAt !== undefined && now >= c.expiresAt) {
          cells[i] = null;
          changed = true;
        }
      }
    }
    return changed;
  }
}
